/**
 * MissionInfoPanel Component
 *
 * Right sidebar panel with tabs for Mission, Waypoints, and Validate
 */

import { useState } from 'react';
import { useMission } from '../hooks/useMission';
import { useWaypoints } from '../hooks/useWaypoints';
import { cn } from '@/lib/utils';
import { Pencil, Trash } from 'lucide-react';

interface MissionInfoPanelProps {
  className?: string;
}

type TabType = 'mission' | 'waypoints' | 'validate';

export function MissionInfoPanel({ className }: MissionInfoPanelProps) {
  const { mission } = useMission();
  const { waypoints } = useWaypoints();
  const [activeTab, setActiveTab] = useState<TabType>('mission');

  if (!mission) return null;

  return (
    <div
      className={cn(
        'bg-slate-900/95 backdrop-blur-sm border border-slate-700 rounded-lg shadow-2xl',
        'flex flex-col h-full overflow-hidden flex-1',
        className
      )}
    >
      {/* Tab Navigation */}
      <div className="flex border-b border-slate-700 bg-slate-800/50">
        <TabButton
          label="Mission"
          isActive={activeTab === 'mission'}
          onClick={() => setActiveTab('mission')}
        />
        <TabButton
          label="Waypoints"
          isActive={activeTab === 'waypoints'}
          onClick={() => setActiveTab('waypoints')}
        />
        <TabButton
          label="Validate"
          isActive={activeTab === 'validate'}
          onClick={() => setActiveTab('validate')}
        />
      </div>

      {/* Tab Content */}
      <div className="flex-1 overflow-y-auto">
        {activeTab === 'mission' && <MissionTab mission={mission} />}
        {activeTab === 'waypoints' && <WaypointsTab waypoints={waypoints} />}
        {activeTab === 'validate' && <ValidateTab mission={mission} />}
      </div>

      {/* Action Buttons */}
      <div className="p-4 border-t border-slate-700 flex gap-3">
        <button className="flex-1 bg-slate-700 hover:bg-slate-600 text-white text-sm font-medium py-2 rounded-md transition-colors">
          Cancel
        </button>
        <button className="flex-1 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium py-2 rounded-md transition-colors">
          Save Mission
        </button>
      </div>
    </div>
  );
}

interface TabButtonProps {
  label: string;
  isActive: boolean;
  onClick: () => void;
}

function TabButton({ label, isActive, onClick }: TabButtonProps) {
  return (
    <button
      onClick={onClick}
      className={cn(
        'flex-1 px-4 py-3 text-sm font-medium transition-colors border-b-2',
        isActive
          ? 'text-blue-400 border-blue-500 bg-slate-800/30'
          : 'text-white border-transparent hover:text-slate-300'
      )}
    >
      {label}
    </button>
  );
}

interface MissionTabProps {
  mission: any;
}

function MissionTab({ mission }: MissionTabProps) {
  return (
    <div className="p-4 space-y-4">
      <div className="flex-shrink-0 rounded-md">
        <div className="bg-slate-800/50 px-3 py-2 rounded-md">
          <label className="text-md text-white block mb-1">Mission Information</label>
          <label className="text-xs text-white tracking-wide">
            Mission Name
          </label>
          <p className="text-sm text-white mt-1 bg-slate-800 px-3 py-2 rounded-md border border-slate-700">
            {mission.name}
          </p>

          <label className="text-xs font-medium text-white tracking-wide">
            Description
          </label>
          <textarea
            defaultValue={mission.description || ''}
            placeholder="Add mission description..."
            className="w-full mt-1 bg-slate-800 text-white rounded-md border border-slate-700 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
            rows={3}
          />
        </div>

      </div>

      <div className="flex-shrink-0 rounded-md">
        <div className="bg-slate-800/50 px-3 py-2 rounded-md">
          <label className="text-md text-white block mb-1">Flight Parameters</label>
          <label className="text-xs font-medium text-white tracking-wide">
            Default Altitude
          </label>
          <div className="flex items-center gap-2 mt-1">
            <input
              type="number"
              defaultValue={mission.defaultAltitude}
              className="flex-1 bg-slate-800 text-white rounded-md border border-slate-700 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            <span className="text-white text-sm">m</span>
          </div>

          <label className="text-xs font-medium text-white tracking-wide">
            Default Speed
          </label>
          <div className="flex items-center gap-2 mt-1">
            <input
              type="number"
              defaultValue={mission.defaultSpeed}
              className="flex-1 bg-slate-800 text-white rounded-md border border-slate-700 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            <span className="text-white text-sm">m/s</span>
          </div>

          <label className="text-xs font-medium text-white tracking-wide">
            Cruise Speed
          </label>
          <div className="flex items-center gap-2 mt-1">
            <input
              type="number"
              defaultValue={mission.defaultSpeed}
              className="flex-1 bg-slate-800 text-white rounded-md border border-slate-700 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            <span className="text-white text-sm">m/s</span>
          </div>
        </div>

      </div>

      <div className="flex-shrink-0 rounded-md">
        <div className="bg-slate-800/50 px-3 py-2 rounded-md">
          <label className="text-md text-white block mb-1">Vehicle Configuration</label>
          <label className="text-xs text-white tracking-wide">
            Vehicle Type
          </label>
          <p className="text-sm text-white mt-1 bg-slate-800 px-2 py-1 rounded-md border border-slate-700">
            {mission.vehicleType === 'copter'
              ? 'Multicopter'
              : mission.vehicleType === 'plane'
                ? 'Fixed Wing'
                : mission.vehicleType.charAt(0).toUpperCase() + mission.vehicleType.slice(1)}
          </p>

          <label className="text-xs text-white tracking-wide">
            Firmware Type
          </label>
          <p className="text-sm text-white mt-1 bg-slate-800 px-2 py-1 rounded-md border border-slate-700">
            {mission.settings?.firmwareType || 'ArduPilot'}
          </p>
        </div>
      </div>

      <div className="flex-shrink-0 rounded-md">
        <div className="bg-slate-800 px-3 py-2 rounded-md">
          <label className="text-md text-white tracking-wide mb-2 block">
            Area of Interest
          </label>
          <div className="w-full text-white text-sm font-medium rounded-md transition-colors ">
            Select Area of Interest
          </div>
          <div className='bg-slate-900/95 px-3 py-2 mt-2 rounded-md space-y-2'>
            <p className="text-sm text-white mt-1 bg-slate-800 px-2 py-1 rounded-md border border-slate-700">
              {mission.vehicleType === 'copter'
                ? 'Multicopter'
                : mission.vehicleType === 'plane'
                  ? 'Fixed Wing'
                  : mission.vehicleType.charAt(0).toUpperCase() + mission.vehicleType.slice(1)}
            </p>
            <p className="text-sm text-white mt-1 bg-slate-800 px-2 py-1 rounded-md border border-slate-700">
              {mission.settings?.firmwareType || 'ArduPilot'}
            </p>
          </div>

        </div>
      </div>
    </div>
  );
}

interface WaypointsTabProps {
  waypoints: any[];
}

function WaypointsTab({ waypoints }: WaypointsTabProps) {
  return (
    <div className="p-2.5">
      {waypoints.length === 0 ? (
        <div className="text-center py-8 text-slate-500">
          <svg className="w-8 h-8 mx-auto mb-2 opacity-50" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
          </svg>
          <p className="text-sm">No waypoints yet</p>
          <p className="text-xs mt-1">Click on the map to add waypoints</p>
        </div>
      ) : (
        <div className="space-y-3">
          {waypoints.map((waypoint, _index) => (
            <div key={waypoint.id} className="flex flex-row  items-center gap-4 justify-between bg-slate-800 rounded-md border border-slate-700 p-3">
              <div className="flex items-start mb-2">
                <div>
                  <h4 className="text-sm font-semibold text-white">
                    Waypoint #{waypoint.sequence + 1}
                  </h4>
                  {waypoint.label && (
                    <p className="text-xs text-white mt-0.5">{waypoint.label}</p>
                  )}
                </div>
              </div>
              <div className="flex flex-col text-xs">
                <div>
                  <p className="text-white">Waypoint #{waypoint.sequence + 1}</p>
                </div>
                <div>
                  <p className="text-white font-mono">{waypoint.latitude.toFixed(6) + `,` + waypoint.longitude.toFixed(6)}</p>
                </div>
                <div className="col-span-2">
                  <span className="text-white">Altitude: {waypoint.altitude.toFixed(1)} m</span>
                </div>
              </div>

               <div className="flex flex-row text-xs text-white gap-2">
                <div>
                  <Pencil className='w-4'/>
                </div>
                <div>
                  <Trash className='w-4'/>
                </div>
              </div>
              {waypoint.notes && (
                <div className="mt-2 pt-2 border-t border-slate-700">
                  <p className="text-xs text-white">Notes</p>
                  <p className="text-xs text-slate-300 mt-1">{waypoint.notes}</p>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

interface ValidateTabProps {
  mission: any;
}

function ValidateTab({ mission }: ValidateTabProps) {
  return (
    <div className="p-4 space-y-4">
      <div className="bg-blue-900/20 border border-blue-700/50 rounded-md p-3">
        <p className="text-sm text-blue-200">
          <span className="font-semibold">Validation Status:</span> Mission validation checks
        </p>
      </div>

      <div className="space-y-2">
        <ValidationItem
          status="success"
          label="Waypoints"
          message={`${mission.waypoints.length} waypoint${mission.waypoints.length !== 1 ? 's' : ''} defined`}
        />
        <ValidationItem
          status="success"
          label="Vehicle Type"
          message={mission.vehicleType === 'copter' ? 'Multicopter' : 'Fixed Wing'}
        />
        <ValidationItem
          status="success"
          label="Flight Parameters"
          message="All parameters configured"
        />
        <ValidationItem
          status="warning"
          label="Battery"
          message="High battery usage detected"
        />
      </div>

      <div className="bg-slate-800 rounded-md border border-slate-700 p-3">
        <h4 className="text-sm font-semibold text-white mb-2">Validation Summary</h4>
        <div className="space-y-1 text-xs text-slate-300">
          <p>✓ Waypoints: Valid</p>
          <p>✓ Vehicle: Configured</p>
          <p>⚠ Battery: High usage</p>
        </div>
      </div>
    </div>
  );
}

interface ValidationItemProps {
  status: 'success' | 'warning' | 'error';
  label: string;
  message: string;
}

function ValidationItem({ status, label, message }: ValidationItemProps) {
  const statusColor = {
    success: 'text-green-400',
    warning: 'text-yellow-400',
    error: 'text-red-400',
  };

  const statusIcon = {
    success: '✓',
    warning: '⚠',
    error: '✕',
  };

  return (
    <div className="flex items-start gap-2 bg-slate-800 rounded-md border border-slate-700 p-2">
      <span className={cn('font-bold text-lg mt-0.5', statusColor[status])}>
        {statusIcon[status]}
      </span>
      <div className="flex-1">
        <p className="text-sm font-medium text-white">{label}</p>
        <p className="text-xs text-white">{message}</p>
      </div>
    </div>
  );
}

